<template>
  <b-card no-body class="border-0">
    <div v-if="!dropZoneRef?.result?.hasFiles" class="d-inline-flex w-100">
      <b-button
        size="sm"
        variant="outline-excel"
        :disabled="loading"
        class="ml-auto"
        @click="getModelPlanilha"
      >
        <font-awesome-icon icon="file-excel" />
        Baixar modelo
      </b-button>
      <b-button
        v-if="displayCurrentBase"
        variant="outline-exitos"
        size="sm"
        class="mr-0"
        :disabled="loading"
        @click="getCurrentData"
      >
        <font-awesome-icon icon="cloud-arrow-down" />
        Baixar base atual
      </b-button>
    </div>

    <view-toggle
      ref="viewToggleRef"
      :initial-value="!!dropZoneRef?.result?.hasFiles"
      class="mt-3"
    >
      <template #default="{ value, off }">
        <b-alert
          :show="value"
          class="bg-deep-blue bg-deep-blue-5 border-0 d-inline-flex align-items-center w-100 mb-0"
        >
          <div class="pl-2 pr-3 flex-shrink-0">
            <font-awesome-icon
              icon="circle-info"
              class="h1 mb-0 text-deep-blue"
            />
          </div>
          <div class="flex-grow-1">
            <h4 class="text-deep-blue font-weight-600 mb-0">
              A importação substituirá os municípios selecionados
            </h4>
            <p class="font-weight-normal text-gray mb-0">
              Ao salvar a oportunidade, todos os municípios atuais serão
              removidos e substituídos pelos municípios válidos do arquivo
              anexado.
            </p>
          </div>

          <b-button
            size="sm"
            class="align-self-center"
            variant="outline-deep-blue"
            @click="off"
          >
            Entendi
          </b-button>
        </b-alert>
      </template>
    </view-toggle>

    <b-overlay
      v-if="isDisplayResult"
      :show="isPending"
      :opacity="0.75"
      blur="1.5"
      variant="white"
      rounded="sm"
      :class="{ 'py-5': isPending }"
    >
      <import-municipios-result
        v-if="!isIdle && !isPending"
        :result="result"
        @view-result="$emit('before')"
        @new-import="newImport"
      />
    </b-overlay>

    <drop-zone
      v-if="!statusBoolean"
      ref="dropZoneRef"
      :accept="getType('application', 'excel')"
      :disabled="statusBoolean"
      class="mt-3"
    >
      <template #default="{ open, reset, result }">
        <drop-plain-file
          v-if="!result.hasFiles"
          :dropping="result.isOverDropzone"
          :disabled="statusBoolean"
          @open="open"
        />
        <drop-selected-file
          v-else
          :file="result.firstFile"
          :disabled="statusBoolean"
          @cancel="reset"
          @replace="open({ reset: true })"
        />
      </template>
    </drop-zone>
    <prompt
      ref="promptRef"
      title="Confirmar importação de municípios"
      :message="`
        Ao continuar, os municípios selecionados atualmente serão substituídos pelos municípios válidos do arquivo importado. Essa alteração será aplicada ao salvar a oportunidade.
      `"
    />
  </b-card>
</template>
<script>
import {
  computed,
  defineComponent,
  inject,
  onBeforeUnmount,
  reactive,
  ref,
  toRefs,
} from "vue";
import { downloadFile } from "@/helpers/functions/LinkHelper";
import { useNotify } from "@/composables/useNotify";
import { useActions, useState } from "vuex-composition-helpers";
import DropZone from "@/components/DropZone.vue";
import Prompt from "@/components/Prompt.vue";
import { getType } from "@/helpers/functions/TypeFileHelper";
import DropPlainFile from "@/modules/oportunidades/Components/DropPlainFile.vue";
import DropSelectedFile from "@/modules/oportunidades/Components/DropSelectedFile.vue";
import ImportMunicipiosResult from "@/modules/oportunidades/Components/ImportMunicipiosResult.vue";
import ViewToggle from "@/components/ViewToggle.vue";
import { ENUM_STATUS_IMPORTER } from "@/modules/oportunidades/constants/statusImporterMunicipios";
import { useDispatchWhen } from "@/composables/useDispatchWhen";
import { usePrompt } from "@/composables/usePrompt";

export default defineComponent({
  components: {
    Prompt,
    DropZone,
    DropPlainFile,
    DropSelectedFile,
    ImportMunicipiosResult,
    ViewToggle,
  },
  emits: ["before"],
  setup() {
    const dropZoneRef = ref();
    const viewToggleRef = ref();
    const promptRef = ref();

    const states = reactive({
      loading: false,
      isDisplayResult: false,
      status: ENUM_STATUS_IMPORTER.IDLE,
      importacao_id: null,

      result: {},
    });

    const rootForm = inject("rootForm");

    const $notifications = useNotify();

    const $prompt = usePrompt(promptRef);

    const $states = useState("oportunidades", ["oportunidade"]);

    const $actions = useActions("oportunidades", [
      "ActionShow",
      "ActionGetModelMunicipios",
      "ActionGetAtualMunicipios",
      "ActionImportarMunicipios",
      "ActionGetStatusMunicipios",
    ]);

    const isInfoActive = computed(() => !!viewToggleRef.value?.value);

    const displayCurrentBase = computed(() => !!$states.oportunidade.value?.id);
    const currentImportacaoId = computed(
      () => $states.oportunidade.value?.importacao_id
    );

    const isPendingImport = computed(() => {
      const existsImportId = !!states.importacao_id;
      const pendingStatus = [
        ENUM_STATUS_IMPORTER.PENDENTE,
        ENUM_STATUS_IMPORTER.PROCESSANDO,
      ];

      return existsImportId && pendingStatus.includes(states.status);
    });

    async function confirmPrompt() {
      if (!isInfoActive.value) return true;

      $prompt.open();
      return $prompt.result();
    }

    async function uploadFile(id) {
      try {
        const result = await confirmPrompt();

        if (!result) return;
        if (!dropZoneRef.value?.result?.firstFile) return;
        if (!dropZoneRef.value?.result?.hasFiles) return;

        viewToggleRef.value?.off?.();

        const payload = {
          id,
          arquivo: dropZoneRef.value?.result?.firstFile,
        };

        const { data } = await $actions.ActionImportarMunicipios(payload);
        states.isDisplayResult = true;

        states.importacao_id = data.importacao_id;
        states.status = data.status;

        dropZoneRef.value.reset?.();
      } catch (error) {
        console.log("error", error);
        $notifications.addNotification({
          message: "Erro ao atualizar base de municípios da oportunidade",
          icon: "exclamation-triangle",
          type: "danger",
        });
      }
    }

    async function getModelPlanilha() {
      try {
        states.loading = true;
        const response = await $actions.ActionGetModelMunicipios();

        downloadFile(response);

        URL.revokeObjectURL(response);
      } catch (error) {
        $notifications.addNotification({
          message: "Erro ao obter modelo de dados",
          icon: "exclamation-triangle",
          type: "danger",
        });
      } finally {
        states.loading = false;
      }
    }

    async function getCurrentData() {
      try {
        states.loading = true;
        const id = $states.oportunidade.value?.id;
        const response = await $actions.ActionGetAtualMunicipios(id);

        downloadFile(response);

        URL.revokeObjectURL(response);
      } catch (error) {
        $notifications.addNotification({
          message: "Erro ao obter dados atuais",
          icon: "exclamation-triangle",
          type: "danger",
        });
      } finally {
        states.loading = false;
      }
    }

    function newImport() {
      reset();
      states.isDisplayResult = false;
    }

    const stopSaveFn = rootForm?.onSave?.((data) => {
      if (!data?.id) return;

      uploadFile(data.id);
    });

    const { onError, onSuccess, onStart, reset, isIdle, isPending, isSuccess } =
      useDispatchWhen(
        isPendingImport,
        async () => {
          const payload = {
            id: $states.oportunidade.value?.id,
            importacaoId: states.importacao_id,
          };

          const { data } = await $actions.ActionGetStatusMunicipios(payload);

          states.result = data;
          states.status = data?.status;

          return data;
        },
        {
          interval: 3 * 1000,
          timeout: 4 * 60 * 1000,
        }
      );

    const statusBoolean = computed(
      () => (isPending.value || isSuccess.value) && states.isDisplayResult
    );

    onStart(() => {
      $notifications.addNotification({
        message: "Processando...",
        type: "primary",
      });
    });

    onSuccess(() => {
      const success = states.status === ENUM_STATUS_IMPORTER.CONCLUIDO;

      $notifications.addNotification({
        message: success
          ? "Importação concluída com sucesso!"
          : "A importação foi concluída com erros.",
        type: success ? "success" : "danger",
      });

      $actions.ActionShow($states.oportunidade.value.id);
    });

    onError(() => {
      $notifications.addNotification({
        message: "Não foi possível consultar o status da importação.",
        icon: "exclamation-triangle",
        type: "danger",
      });
    });

    onBeforeUnmount(() => {
      stopSaveFn?.();
    });

    return {
      ...toRefs(states),
      displayCurrentBase,
      dropZoneRef,
      viewToggleRef,
      promptRef,
      currentImportacaoId,
      isPendingImport,
      isIdle,
      isPending,
      isSuccess,
      statusBoolean,
      getCurrentData,
      getModelPlanilha,
      getType,
      uploadFile,
      newImport,
    };
  },
});
</script>
